import {
    createContext, useState, useEffect
} from 'react';
import {
    useIdleTimer
} from 'react-idle-timer';

import {
    httpRequest
} from 'shared/utils/httpsRequest';
import {
    assignUserRole
} from 'shared/utils/organizationUtils';
import {
    authService, organizationService
} from 'shared/api';
import {
    registerNotifier
} from 'shared/api/notifier';
import type {
    AdminUserMinimal,
    GlobalAction,
    GlobalStateValue,
    NotificationItem,
    Organization,
    UserMinimal
} from './context.types';

const defaultGlobalState: GlobalStateValue = {
    actions: () => {
        // Default empty action handler
    },
    loading: true,
    user: {},
    isAuth: false,
    notificationStatus: [],
    isAdminAuth: false,
    userAdmin: {},
    addNewNotifcation: () => {
        // Default empty notification handler
    },
    organizations: []
};

export const Context = createContext<GlobalStateValue>(defaultGlobalState);

const IDLE_USER_TIMEOUT = 14400000; // 4 Hours
const IDLE_USER_THROTTLE = 500;

export default function GlobalState(): GlobalStateValue {
    const [isAuth, setIsAuth] = useState<boolean>(false);
    const [isAdminAuth, setIsAdminAuth] = useState<boolean>(false);
    const [user, setUser] = useState<UserMinimal | Record<string, never>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [notificationStatus, setNotificationStatus] = useState<NotificationItem[]>([]);
    const [userAdmin, setUserAdmin] = useState<AdminUserMinimal | Record<string, never>>({});
    const [organizations, setOrganizations] = useState<Organization[]>([]);

    // Hooks
    useIdleTimer({
        onActive: () => fetchUserDetails(),
        timeout: IDLE_USER_TIMEOUT,
        throttle: IDLE_USER_THROTTLE,
    });

    useEffect(() => {
        const timeOut = setInterval(() => {
            if (notificationStatus.length > 0) {
                setNotificationStatus((prev) =>
                    prev
                        .filter((i) => i.timeremaining > 0)
                        .map((item) => {
                            return {
                                ...item,
                                timeremaining: item.timeremaining - 0.2,
                            };
                        })
                );
            }
        }, 200);

        if (notificationStatus.length <= 0) {
            clearInterval(timeOut);
        }

        return () => clearInterval(timeOut);
    }, [notificationStatus]);

    useEffect(() => {
        if (window.location.pathname.includes('secure')) {
            httpRequest
                .get('/admin/is-admin-auth')
                .then(async (res) => {
                    if (res.data.auth) {
                        setUserAdmin(res.data.admin);
                    }
                    setIsAdminAuth(res.data.auth);

                })
                .catch(() => {
                    setUser({});
                    setIsAdminAuth(false);

                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            fetchUserDetails();
        }
    }, []);

    // Actions
    const addNewNotifcation = (message: string, type: string, timeremaining = 4) => {
        setNotificationStatus((prev) => [
            ...prev,
            {
                message,
                type,
                timeremaining
            },
        ]);
    };

    // Let non-React code (axios error interceptors) raise notifications.
    useEffect(() => {
        registerNotifier(addNewNotifcation);
    }, []);

    const actions = (action: GlobalAction) => {
        const {
            type, payload
        } = action;

        switch (type) {
            case 'SET_LOADING':
                return setLoading(payload);
            case 'SET_USER':
                return setUser(payload);
            case 'SET_IS_AUTH':
                return setIsAuth(payload);
            case 'SET_NOTIFCATION_STATUS':
                return setNotificationStatus(payload);
            case 'SET_IS_ADMIN_AUTH':
                return setIsAdminAuth(payload);
            case 'SET_USER_ADMIN':
                return setUserAdmin(payload);
            case 'SET_ORGANIZATIONS':
                return setOrganizations(payload);
            default:
                return loading;
        }
    };

    const fetchUserDetails = async () => {
        try {
            const res = await authService.getUser();
            if (res.data.auth) {
                setUser(res.data.user as UserMinimal);
                const organizationsResponse = await organizationService.getOrganizations(res.data.user._id);
                // assignUserRole presumably annotates organizations with a role for this user
                setOrganizations(assignUserRole(res.data.user._id, organizationsResponse.data) as Organization[]);
            } else {
                setUser({});
                setIsAuth(false);
            }
            setIsAuth(res.data.auth);

            // Show password expiry warning if stored from login
            if (res.data.auth) {
                const expiryWarning = sessionStorage.getItem('password-expiry-warning');
                if (expiryWarning) {
                    sessionStorage.removeItem('password-expiry-warning');
                    addNewNotifcation(expiryWarning, 'warning', 8);
                }
            }

        } catch (err: any) {
            // Only an explicit auth verdict (401/403) logs the user out.
            // Transient failures — network blips, server restarts, the
            // 503 the auth check returns on backend hiccups — must NOT
            // destroy an authoring session: a hard redirect to /auth/login
            // loses unsaved editor state (production sweep 2026-06-12).
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                setUser({});
                setIsAuth(false);
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        actions,
        loading,
        user,
        isAuth,
        notificationStatus,
        isAdminAuth,
        userAdmin,
        addNewNotifcation,
        organizations
    };
}
